import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useRouteAuthorisation, useCurrentUser, refreshCurrentLegacyFrame } from '@economic/react-app';

import HashedRoute from './components/HashedRoute';
import { legacyForms } from '../pages/sales/invoicing/invoices/utils';

import BookDraftInvoiceDialogContainer from '../pages/sales/invoicing/invoices/book-and-send/book-and-send-single/BookDraftInvoiceDialogContainer';
import BulkBookDraftInvoicesDialogContainer from '../pages/sales/invoicing/invoices/book-and-send/book-and-send-bulk/BulkBookDraftInvoicesDialogContainer';
import CompanyDeregisterDialog from './components/modals/CompanyDeregisterDialog';
import CopyAccrualsDialog from '../pages/sales/invoicing/CopyAccrualsDialog';
import CopyDistributionKeyDialog from '../pages/settings/categories-and-units/departments/CopyDistributionKeyDialog';
import CopyInvoice from '../pages/sales/invoicing/archive/copy-invoice/CopyInvoice';
import CreateDistributionKeyDialog from '../pages/settings/categories-and-units/departments/CreateDistributionKeyDialog';
import EditDistributionKeyDialog from '../pages/settings/categories-and-units/departments/EditDistributionKeyDialog';
import MoneyflowDialog from '../pages/sales/invoicing/invoices/book-and-send/moneyflow/MoneyflowDialog';
import MoneyflowMissingDefaultEinvoiceDialog from '../pages/sales/invoicing/invoices/book-and-send/moneyflow/MoneyflowMissingDefaultEinvoiceDialog';
import MoneyflowOnboardingDialog from '../pages/market/onboarding/Moneyflow';
import MoneyflowReviewOfferDialog from '../pages/sales/invoicing/invoices/book-and-send/moneyflow/MoneyflowReviewOfferDialog';
import ResendInvoiceDialogContainer from '../pages/sales/invoicing/sent/resend-invoice/ResendInvoiceDialogContainer';
import SendDraftQuoteDialogContainer from '../pages/sales/invoicing/quotes/send-quote/SendDraftQuoteDialogContainer';
import SendInvoiceDialogContainer from '../pages/sales/invoicing/archive/send-invoice/SendInvoiceDialogContainer';
import SendInvoicesDialogContainer from '../pages/sales/invoicing/archive/send-invoices/SendInvoicesDialogContainer';
import SendOrderDialogContainer from '../pages/sales/invoicing/orders/send-order/SendOrderDialogContainer';
import { BulkDiscountDialog } from '../pages/sales/invoicing/shared/components/BulkDiscountDialog';
import { ResendQuoteDialogContainer } from '../pages/sales/invoicing/sent/resend-quote/ResendQuoteDialogContainer';
import { ResendMultipleInvoicesDialogContainer } from '../pages/sales/invoicing/sent/resend-multiple-invoices/ResendMultipleInvoicesDialogContainer';
import { ResendOrderDialogContainer } from '../pages/sales/invoicing/sent/resend-order/ResendOrderDialogContainer';
import SendReminderEmailFromArchiveDialogContainer from '../pages/sales/invoicing/archive/send-reminder/SendReminderEmailFromArchiveDialogContainer';
import ResendPurchaseOrderDialogContainer from '../pages/inventory/purchases/sent/resend-purchase-order/ResendPurchaseOrderDialogContainer';
import ResendReminderDialogContainer from '../pages/sales/invoicing/sent/resend-reminder/ResendReminderDialogContainer';
import { ResendStatementOfAccount } from '../pages/sales/invoicing/sent/resend-statement-of-account/ResendStatementOfAccount';
import InventoryFeedbackGroupOnboarding from '../pages/inventory/InventoryFeedbackGroupOnboarding';
import { BulkDepartmentDialog } from '../pages/sales/invoicing/shared/components/BulkDepartmentDialog';
import DeregisterAdministratorDialog from '../pages/system/administrators/DeregisterAdministratorDialog';
import CancelAministratorDeregistrationDialog from '../pages/system/administrators/CancelAdministratorDeregistrationDialog';
import SignupModal from '../pages/accounting/bank/smartpay/components/SignupModal';
import BookAndSendReminderDialogContainer from '../pages/sales/invoicing/reminders/book-and-send-single/BookAndSendReminderDialogContainer';
import BulkBookAndSendReminderDialogContainer from '../pages/sales/invoicing/reminders/book-and-send-bulk/BulkBookAndSendReminderDialogContainer';
import SendPurchaseOrderDialogContainer from '../pages/inventory/purchases/shared/SendPurchaseOrderDialogContainer';
import SendSingleCustomerLedgerCardDialogContainer from '../pages/sales/customers/ledger-cards/send-single/SendSingleCustomerLedgerCardDialogContainer';
import SendBulkCustomerLedgerCardsDialogContainer from '../pages/sales/customers/ledger-cards/send-bulk/SendBulkCustomerLedgerCardsDialogContainer';

export default function LegacyDialogRoutes() {
    const navigate = useNavigate();
    const location = useLocation();
    const user = useCurrentUser();
    const [isProjectManager] = useRouteAuthorisation('/projects/responsibility/invoices');

    const prefix = user.isBasicUser ? '/basic' : '';

    function removeHashFragmentAndRefresh() {
        navigate(`${prefix}/sales/invoicing/invoices`);
        refreshCurrentLegacyFrame('content');
    }

    function navigateAfterBooking(invoiceId: number): void {
        const urlParams = new URLSearchParams(location.search);
        const returnTo = urlParams.get('returnTo');

        switch (returnTo) {
            case legacyForms.quotations:
                navigate(`${prefix}/sales/invoicing/quotations`);
                break;

            case legacyForms.orders:
                navigate(`${prefix}/sales/invoicing/orders`);
                break;

            case legacyForms.archive:
                navigate(`${prefix}/sales/invoicing/archive/invoices?${invoiceId ? `fakturanr=${invoiceId}` : ''}`);
                break;

            case legacyForms.customers:
                navigate(`${prefix}/sales/customers`);
                break;

            case legacyForms.customerProjects:
                navigate(`${prefix}/sales/customers/projects`);
                break;

            default:
                if (isProjectManager) {
                    navigate(`/projects/responsibility/invoices`);
                } else {
                    navigate(`${prefix}/sales/invoicing/invoices`);
                }
                break;
        }
    }

    return (
        <>
            <HashedRoute hash="#book-draft">
                <BookDraftInvoiceDialogContainer onBooked={navigateAfterBooking} />
            </HashedRoute>

            <HashedRoute hash="#book-bulk">
                <BulkBookDraftInvoicesDialogContainer onBooked={removeHashFragmentAndRefresh} />
            </HashedRoute>

            <HashedRoute hash="#book-reminder">
                <BookAndSendReminderDialogContainer />
            </HashedRoute>

            <HashedRoute hash="#book-bulk-reminder">
                <BulkBookAndSendReminderDialogContainer />
            </HashedRoute>

            <HashedRoute hash="#bulk-discount">
                <BulkDiscountDialog onSubmitted={() => refreshCurrentLegacyFrame('content')} />
            </HashedRoute>

            <HashedRoute hash="#bulk-department">
                <BulkDepartmentDialog onSubmitted={() => refreshCurrentLegacyFrame('content')} />
            </HashedRoute>

            <HashedRoute hash="#moneyflow">
                <MoneyflowDialog />
            </HashedRoute>

            <HashedRoute hash="#moneyflow-review-offer">
                <MoneyflowReviewOfferDialog />
            </HashedRoute>

            <HashedRoute hash="#moneyflow-missing-defaulteinvoice">
                <MoneyflowMissingDefaultEinvoiceDialog />
            </HashedRoute>

            <HashedRoute hash="#resend-invoice">
                <ResendInvoiceDialogContainer />
            </HashedRoute>

            <HashedRoute hash="#resend-purchase-order">
                <ResendPurchaseOrderDialogContainer />
            </HashedRoute>

            <HashedRoute hash="#resend-multipleinvoices">
                <ResendMultipleInvoicesDialogContainer />
            </HashedRoute>

            <HashedRoute hash="#book-quote">
                <SendDraftQuoteDialogContainer />
            </HashedRoute>

            <HashedRoute hash="#resend-quote">
                <ResendQuoteDialogContainer />
            </HashedRoute>

            <HashedRoute hash="#book-order">
                <SendOrderDialogContainer />
            </HashedRoute>

            <HashedRoute hash="#resend-order">
                <ResendOrderDialogContainer />
            </HashedRoute>

            <HashedRoute hash="#resend-reminder">
                <ResendReminderDialogContainer />
            </HashedRoute>

            <HashedRoute hash="#resend-statementofaccount">
                <ResendStatementOfAccount />
            </HashedRoute>

            <HashedRoute hash="#copy-invoice">
                <CopyInvoice />
            </HashedRoute>

            <HashedRoute hash="#send-invoice">
                <SendInvoiceDialogContainer />
            </HashedRoute>

            <HashedRoute hash="#send-invoices">
                <SendInvoicesDialogContainer />
            </HashedRoute>

            <HashedRoute hash="#send-reminder-email-from-archive">
                <SendReminderEmailFromArchiveDialogContainer />
            </HashedRoute>

            <HashedRoute hash="#send-single-ledger-card">
                <SendSingleCustomerLedgerCardDialogContainer />
            </HashedRoute>

            <HashedRoute hash="#send-bulk-ledger-cards">
                <SendBulkCustomerLedgerCardsDialogContainer />
            </HashedRoute>

            <HashedRoute hash="#onboardMoneyflow">
                <MoneyflowOnboardingDialog />
            </HashedRoute>

            <HashedRoute hash="#onboardSmartPay">
                <SignupModal inMarket={true} modalProps={{ visible: true }} />
            </HashedRoute>

            <HashedRoute hash="#deregister-company">
                <CompanyDeregisterDialog />
            </HashedRoute>

            <HashedRoute hash="#create-distribution-key">
                <CreateDistributionKeyDialog />
            </HashedRoute>

            <HashedRoute hash="#edit-distribution-key">
                <EditDistributionKeyDialog />
            </HashedRoute>

            <HashedRoute hash="#copy-distribution-key">
                <CopyDistributionKeyDialog />
            </HashedRoute>

            <HashedRoute hash="#copy-accruals">
                <CopyAccrualsDialog />
            </HashedRoute>

            <HashedRoute hash="#inventory-feedback">
                <InventoryFeedbackGroupOnboarding />
            </HashedRoute>

            <HashedRoute hash="#deregister-administrator">
                <DeregisterAdministratorDialog />
            </HashedRoute>

            <HashedRoute hash="#cancel-administrator-deregistration">
                <CancelAministratorDeregistrationDialog />
            </HashedRoute>

            <HashedRoute hash="#send-purchase-order">
                <SendPurchaseOrderDialogContainer />
            </HashedRoute>
        </>
    );
}
